import {
  CompressOutlined,
  ExpandOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { Icon, Tooltip } from "@indigo-components";
import { useAtomValue } from "jotai";
import {
  AnimationEvent,
  FC,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { indyLauncherPositionAtom } from "~/atoms/chatbot";
import { useAnimatedUnmount } from "~/hooks/useAnimatedUnmount";
import { type ChatContext } from "~/hooks/useChatbotContext";
import {
  ACTION_BTN_STYLE,
  IndyWordmark,
  PANEL_SIDEBAR_WIDTH as SIDEBAR_WIDTH,
} from "~/pages/indy/components/chatSurfaceShared";
import { IndyChatSurface } from "~/pages/indy/components/IndyChatSurface";
import { useChatbotRendering } from "~/pages/indy/useChatbotMarkdownComponents";
import { useUserFirstName } from "~/pages/indy/useUserFirstName";
import { IS_MAC } from "~/utils/platform";
import { useAgentChat } from "../hooks/useAgentChat";
import {
  type AmaGuideOpenRequest,
  type AmaGuideOpenResult,
  type AppointmentDocumentOpenRequest,
} from "./chatbotLinks";
import { useAvailableSkills } from "./chatbotSkills/useAvailableSkills";
import { IndyGlassesPicker } from "./IndyGlassesPicker";
import { panelPositionForLauncher } from "./indyLauncherPosition";

/** Exit is a touch quicker than the 360ms entrance — leaving should not
 *  linger. Feeds the CSS keyframe; the unmount waits on `animationend`, with
 *  a `+400ms` fallback timer for tabs hidden mid-exit (frozen animations
 *  never fire the event). */
const PANEL_EXIT_MS = 240;

interface ChatbotModalProps {
  open: boolean;
  onClose: () => void;
  context: ChatContext | null;
  /**
   * Optional callback invoked when the user clicks an AMA Guide reference
   * inside a chatbot response (e.g. "Table 16-3"). Consumers mounted next
   * to an AMA PDF viewer (currently only the IR sidebar) wire this to the
   * same `openRequest` contract used by `LearnMoreSection`. When absent,
   * the click falls back to the cross-surface atom.
   */
  onRequestAmaGuide?: (
    req: AmaGuideOpenRequest,
  ) => Promise<AmaGuideOpenResult> | void;
  /**
   * Optional callback invoked when the user clicks an
   * `indigo://appointment-doc?…` link inside a chatbot response.
   */
  onRequestAppointmentDocument?: (req: AppointmentDocumentOpenRequest) => void;
}

const PANEL_WIDTH = 420;
const PANEL_HEIGHT = 520;
const MIN_PANEL_WIDTH = 300;
const MIN_PANEL_HEIGHT = 320;

/** Clamp a value between min and max. */
function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

const ChatbotModal: FC<ChatbotModalProps> = ({
  open,
  onClose,
  context,
  onRequestAmaGuide,
  onRequestAppointmentDocument,
}) => {
  // Skills hook is read before `useAgentChat` so its `refresh` callback can be
  // threaded into the SSE handler — Indy's create/update/archive tools emit a
  // `skill_mutated` event mid-conversation, and we want slash-autocomplete to
  // reflect the change without waiting for a reopen.
  const {
    skills: availableSkills,
    refresh: forceRefreshAvailableSkills,
    refreshIfStale: refreshAvailableSkills,
  } = useAvailableSkills();
  const handleSkillMutated = useCallback(() => {
    void forceRefreshAvailableSkills();
  }, [forceRefreshAvailableSkills]);

  const chat = useAgentChat(
    context?.type === "appointment" ? context.id : null,
    open,
    {
      indigoDocId: context?.type === "indigo_doc" ? context.id : null,
      onSkillMutated: handleSkillMutated,
    },
  );

  const { markdownComponents, linkContext } = useChatbotRendering({
    onRequestAmaGuide,
    onRequestAppointmentDocument,
  });
  const userFirstName = useUserFirstName();

  // --- Sidebar (foldable in panel mode) ---
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Stores x position before sidebar opened so we can restore it on close.
  const preSidebarXRef = useRef<number | null>(null);

  // --- Panel geometry / drag / resize ---
  const [panelWidth, setPanelWidth] = useState(PANEL_WIDTH);
  // Exit-through-unmount protocol lives in the shared hook (see its doc for
  // the traps: seeded mount, derived `exiting`, reduced-motion same-tick,
  // hidden-tab timer fallback, `resumed` on reopen-mid-exit). The +400ms is
  // slack: `animationend` below is the normal finish, the hook's timer only
  // catches a hidden tab whose frozen animation never fires the event.
  const { mounted, exiting, resumed, unmountNow } = useAnimatedUnmount(
    open,
    PANEL_EXIT_MS + 400,
  );
  // Unmount when the exit animation reports done — not a parallel setTimeout,
  // which duplicated the duration in JS and CSS and could drift. Animation
  // events bubble, so filter to the panel's own exit.
  const handlePanelAnimationEnd = useCallback(
    (e: AnimationEvent<HTMLDivElement>) => {
      if (e.animationName !== "chatbot-panel-exit") return;
      unmountNow();
    },
    [unmountNow],
  );

  const [panelHeight, setPanelHeight] = useState(PANEL_HEIGHT);
  // Where the user parked the launcher. The panel opens off that corner rather
  // than off a hard-coded one, so someone who dragged the button clear of the
  // medical-record page numbers does not get the panel dropped on them
  // instead. Untouched, this resolves to the old bottom-right default exactly.
  const launcherPosition = useAtomValue(indyLauncherPositionAtom);
  const [position, setPosition] = useState(() =>
    panelPositionForLauncher(launcherPosition, PANEL_WIDTH, PANEL_HEIGHT),
  );

  // --- Expand / near-fullscreen state ---
  const [isExpanded, setIsExpanded] = useState(false);
  const [isAnimatingExpand, setIsAnimatingExpand] = useState(false);
  const preExpandRef = useRef<{
    x: number;
    y: number;
    width: number;
    height: number;
    sidebarWasOpen: boolean;
  } | null>(null);

  // Re-anchor to the launcher on each fresh open. Deliberately only on the
  // false→true edge, so a panel dragged or resized mid-conversation stays put;
  // the trade is that a panel moved and then closed returns to the launcher
  // next time, which is the behaviour the launcher's position is there to
  // promise. Skipped while expanded — that state owns its own centred geometry.
  const wasOpenRef = useRef(open);
  useEffect(() => {
    const wasOpen = wasOpenRef.current;
    wasOpenRef.current = open;
    if (!open || wasOpen || isExpanded) return;
    setPosition(
      panelPositionForLauncher(launcherPosition, panelWidth, panelHeight),
    );
  }, [open, isExpanded, launcherPosition, panelWidth, panelHeight]);

  // When expanded, sidebar lives *inside* the panel; when collapsed, it adds to it.
  const effectiveWidth = isExpanded
    ? panelWidth
    : sidebarOpen
      ? panelWidth + SIDEBAR_WIDTH
      : panelWidth;

  const dragState = useRef<{
    startX: number;
    startY: number;
    origX: number;
    origY: number;
  } | null>(null);

  const handleDragStart = useCallback(
    (e: React.MouseEvent) => {
      // Don't start drag on interactive elements inside the title bar.
      if ((e.target as HTMLElement).closest("button, .ant-dropdown-trigger"))
        return;
      if (isExpanded) return;
      e.preventDefault();
      dragState.current = {
        startX: e.clientX,
        startY: e.clientY,
        origX: position.x,
        origY: position.y,
      };

      const handleDragMove = (ev: MouseEvent) => {
        if (!dragState.current) return;
        const dx = ev.clientX - dragState.current.startX;
        const dy = ev.clientY - dragState.current.startY;
        setPosition({
          x: clamp(
            dragState.current.origX + dx,
            0,
            window.innerWidth - effectiveWidth,
          ),
          y: clamp(dragState.current.origY + dy, 0, window.innerHeight - 48),
        });
      };

      const handleDragEnd = () => {
        dragState.current = null;
        window.removeEventListener("mousemove", handleDragMove);
        window.removeEventListener("mouseup", handleDragEnd);
      };

      window.addEventListener("mousemove", handleDragMove);
      window.addEventListener("mouseup", handleDragEnd);
    },
    [position, effectiveWidth, isExpanded],
  );

  // Keep the panel inside the viewport when the window shrinks. Size is
  // clamped before position: a panel left wider or taller than the new
  // viewport can't be brought back into view by moving it alone.
  useEffect(() => {
    const handleResize = () => {
      if (isExpanded) {
        const expandedW = Math.round(window.innerWidth * 0.9);
        const expandedH = Math.round(window.innerHeight * 0.9);
        setPanelWidth(expandedW);
        setPanelHeight(expandedH);
        setPosition({
          x: Math.round((window.innerWidth - expandedW) / 2),
          y: Math.round((window.innerHeight - expandedH) / 2),
        });
      } else {
        const sidebarExtra = sidebarOpen ? SIDEBAR_WIDTH : 0;
        const nextWidth = clamp(
          panelWidth,
          MIN_PANEL_WIDTH,
          Math.max(MIN_PANEL_WIDTH, window.innerWidth - sidebarExtra),
        );
        const nextHeight = clamp(
          panelHeight,
          MIN_PANEL_HEIGHT,
          Math.max(MIN_PANEL_HEIGHT, window.innerHeight),
        );

        setPanelWidth(nextWidth);
        setPanelHeight(nextHeight);
        setPosition((prev) => ({
          x: clamp(prev.x, 0, window.innerWidth - nextWidth - sidebarExtra),
          y: clamp(prev.y, 0, window.innerHeight - nextHeight),
        }));
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isExpanded, sidebarOpen, panelWidth, panelHeight]);

  // Toggle sidebar and adjust position to keep panel in viewport.
  const toggleSidebar = useCallback(() => {
    setSidebarOpen((prev) => {
      const next = !prev;
      // When expanded, sidebar is inside the panel — no position/size change.
      if (!isExpanded) {
        if (next) {
          setPosition((pos) => {
            preSidebarXRef.current = pos.x;
            return {
              x: clamp(
                pos.x,
                0,
                window.innerWidth - panelWidth - SIDEBAR_WIDTH,
              ),
              y: pos.y,
            };
          });
        } else {
          setPosition((pos) => ({
            x: preSidebarXRef.current ?? pos.x,
            y: pos.y,
          }));
          preSidebarXRef.current = null;
        }
      }
      return next;
    });
  }, [panelWidth, isExpanded]);

  // Toggle expand / collapse to near-fullscreen.
  const toggleExpand = useCallback(() => {
    setIsAnimatingExpand(true);
    setTimeout(() => setIsAnimatingExpand(false), 300);

    if (isExpanded) {
      const saved = preExpandRef.current;
      if (saved) {
        setPanelWidth(saved.width);
        setPanelHeight(saved.height);
        if (!saved.sidebarWasOpen && sidebarOpen) {
          setSidebarOpen(false);
          setPosition({ x: saved.x, y: saved.y });
        } else if (saved.sidebarWasOpen && sidebarOpen) {
          setPosition({
            x: clamp(
              saved.x,
              0,
              window.innerWidth - saved.width - SIDEBAR_WIDTH,
            ),
            y: saved.y,
          });
        } else {
          setPosition({ x: saved.x, y: saved.y });
        }
      }
      preSidebarXRef.current = null;
      setIsExpanded(false);
    } else {
      preExpandRef.current = {
        x: preSidebarXRef.current ?? position.x,
        y: position.y,
        width: panelWidth,
        height: panelHeight,
        sidebarWasOpen: sidebarOpen,
      };
      const expandedW = Math.round(window.innerWidth * 0.9);
      const expandedH = Math.round(window.innerHeight * 0.9);
      setPanelWidth(expandedW);
      setPanelHeight(expandedH);
      setPosition({
        x: Math.round((window.innerWidth - expandedW) / 2),
        y: Math.round((window.innerHeight - expandedH) / 2),
      });
      setIsExpanded(true);
      setSidebarOpen(true);
    }
  }, [isExpanded, position, panelWidth, panelHeight, sidebarOpen]);

  // Keyboard shortcut: Cmd/Ctrl + Shift + E to toggle expand.
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === "E") {
        e.preventDefault();
        toggleExpand();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, toggleExpand]);

  // Reset to collapsed mode once the exit has *finished*, so a panel that
  // truly closed starts fresh. Gated on `mounted`, not `open`: on close the
  // panel stays painted through the exit animation, and resetting geometry
  // then visibly teleported the expanded panel to a corner box before the
  // fade. Deliberate consequence: a reopen that catches the exit (within
  // PANEL_EXIT_MS) never unmounts, so it RESUMES with geometry intact — one
  // rule, "fresh after a real close, resumed if you caught it", matching the
  // visual resume (`resumed` suppresses the entrance replay).
  useEffect(() => {
    if (!mounted && isExpanded) {
      setIsAnimatingExpand(false);
      setIsExpanded(false);
      setSidebarOpen(false);
      setPanelWidth(PANEL_WIDTH);
      setPanelHeight(PANEL_HEIGHT);
      setPosition(
        panelPositionForLauncher(launcherPosition, PANEL_WIDTH, PANEL_HEIGHT),
      );
      preExpandRef.current = null;
      preSidebarXRef.current = null;
    }
  }, [mounted, isExpanded, launcherPosition]);

  // ── Panel resize via edge / corner drag ──
  const handleResizeStart = useCallback(
    (edges: {
      top?: boolean;
      right?: boolean;
      bottom?: boolean;
      left?: boolean;
    }) =>
      (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        const startX = e.clientX;
        const startY = e.clientY;
        const origW = panelWidth;
        const origH = panelHeight;
        const origX = position.x;
        const origY = position.y;

        // The rendered box is `panelWidth` plus the sidebar when it is docked
        // beside the panel, so the width budget has to account for it.
        const sidebarExtra = sidebarOpen ? SIDEBAR_WIDTH : 0;
        // Widest/tallest the panel may grow before it leaves the viewport. If
        // the panel already starts out of bounds these can fall below the
        // minimums, in which case the minimum wins and the drag simply cannot
        // make things worse.
        const maxW = window.innerWidth - origX - sidebarExtra;
        const maxH = window.innerHeight - origY;

        const onMove = (ev: MouseEvent) => {
          const dx = ev.clientX - startX;
          const dy = ev.clientY - startY;
          let w = origW;
          let h = origH;
          let x = origX;
          let y = origY;

          if (edges.right) w = clamp(origW + dx, MIN_PANEL_WIDTH, maxW);
          if (edges.left) {
            // Lower bound keeps the left edge at x >= 0; upper bound keeps the
            // panel at least MIN_PANEL_WIDTH wide.
            const d = clamp(dx, -origX, origW - MIN_PANEL_WIDTH);
            w = origW - d;
            x = origX + d;
          }
          if (edges.bottom) h = clamp(origH + dy, MIN_PANEL_HEIGHT, maxH);
          if (edges.top) {
            // Lower bound keeps the title bar at y >= 0 so the close button
            // stays reachable; upper bound keeps the minimum height.
            const d = clamp(dy, -origY, origH - MIN_PANEL_HEIGHT);
            h = origH - d;
            y = origY + d;
          }

          setPanelWidth(w);
          setPanelHeight(h);
          setPosition({ x, y });
        };

        const onUp = () => {
          window.removeEventListener("mousemove", onMove);
          window.removeEventListener("mouseup", onUp);
        };

        window.addEventListener("mousemove", onMove);
        window.addEventListener("mouseup", onUp);
      },
    [panelWidth, panelHeight, position, sidebarOpen],
  );

  // Hold the panel mounted through its exit. `open` flipping false used to
  // return null on the same tick, so the panel vanished on close while it eased
  // in on open — the asymmetry read as a glitch rather than a choice.
  if (!mounted) return null;

  const titleBar = (
    <div
      onMouseDown={handleDragStart}
      className="flex items-center justify-between px-4 py-3"
      style={{
        borderBottom: "1px solid var(--indigo-border-default)",
        cursor: isExpanded ? "default" : "grab",
        userSelect: "none",
        flexShrink: 0,
      }}
    >
      <div
        className="flex items-center gap-1.5"
        style={{ minWidth: 0, cursor: "default" }}
      >
        <Tooltip
          content={sidebarOpen ? "Close sidebar" : "Chat history"}
          placement="top"
          trigger="mouseenter"
        >
          <button
            className="chatbot-action-btn"
            style={ACTION_BTN_STYLE}
            onClick={toggleSidebar}
          >
            {sidebarOpen ? (
              <MenuFoldOutlined style={{ fontSize: 13 }} />
            ) : (
              <MenuUnfoldOutlined style={{ fontSize: 13 }} />
            )}
          </button>
        </Tooltip>
        <div className="inline-flex items-center gap-1.5 select-none min-w-0">
          <IndyGlassesPicker />
          <IndyWordmark fontSize={16} color="var(--indigo-text-primary)" />
          {context && (
            // `truncate` needs a block box — ellipsis does not apply to a flex
            // container, so the label is clamped on an inner span instead.
            <span
              className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] leading-none min-w-0"
              style={{
                backgroundColor: "var(--indigo-gray-100)",
                color: "var(--indigo-text-secondary)",
              }}
            >
              {/* `overflow` clips to the padding box, and `leading-none` makes
                  that exactly the 10px line box — enough to shave descenders off
                  a `g`/`y`. Give the clip rect some headroom. */}
              <span className="truncate leading-[1.4]">@ {context.label}</span>
            </span>
          )}
        </div>
      </div>
      <div
        className="flex items-center gap-0.5"
        style={{ flexShrink: 0, cursor: "default" }}
      >
        <Tooltip content="New Chat" placement="top" trigger="mouseenter">
          <button
            className="chatbot-action-btn"
            style={ACTION_BTN_STYLE}
            onClick={chat.startNewChat}
            disabled={chat.isStreaming}
          >
            <PlusOutlined style={{ fontSize: 11 }} />
          </button>
        </Tooltip>
        <Tooltip
          // The chord is (Cmd|Ctrl)+Shift+E; show the glyphs the keyboard
          // in front of the user actually has.
          content={
            isExpanded
              ? IS_MAC
                ? "Collapse (⌘⇧E)"
                : "Collapse (Ctrl+Shift+E)"
              : IS_MAC
                ? "Expand (⌘⇧E)"
                : "Expand (Ctrl+Shift+E)"
          }
          placement="top"
          trigger="mouseenter"
        >
          <button
            className="chatbot-action-btn"
            style={ACTION_BTN_STYLE}
            onClick={toggleExpand}
          >
            {isExpanded ? (
              <CompressOutlined style={{ fontSize: 11 }} />
            ) : (
              <ExpandOutlined style={{ fontSize: 11 }} />
            )}
          </button>
        </Tooltip>
        <button
          className="chatbot-action-btn"
          style={ACTION_BTN_STYLE}
          onClick={onClose}
        >
          <Icon name="x-mark" size="xs" />
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* Panel-only entrance + transition animations. The rest of the chat
          surface styling lives in IndyChatSurface.css (scoped to
          `.indy-chat-surface`). */}
      <style>{`
      .chatbot-floating-panel { animation: chatbot-panel-enter 0.36s var(--indigo-anim-easing-standard, ease-out) both; }
      .chatbot-panel-animating { transition: left 0.25s ease, top 0.25s ease, width 0.25s ease, height 0.25s ease; }
      @keyframes chatbot-panel-enter { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
      /* Mirror of the entrance, on the exit easing (accelerates away). Compound
         selector so it beats the entrance rule above; inert while it plays. */
      .chatbot-floating-panel.chatbot-panel-exiting { animation: chatbot-panel-exit ${PANEL_EXIT_MS}ms var(--indigo-anim-easing-exit, ease-in) both; pointer-events: none; }
      /* Reopened mid-exit: removing the exiting class would re-expose the
         entrance as a NEW animation, snapping the half-faded panel to the
         keyframes' opacity-0 start before replaying 360ms — a backwards
         flash. Same node, so no entrance: it returns at full opacity. */
      .chatbot-floating-panel.chatbot-panel-resumed { animation: none; }
      @keyframes chatbot-panel-exit { from { opacity: 1; transform: translateY(0); } to { opacity: 0; transform: translateY(10px); } }
      @media (prefers-reduced-motion: reduce) {
        .chatbot-floating-panel, .chatbot-floating-panel.chatbot-panel-exiting { animation: none; }
      }
    `}</style>
      <div
        className={`chatbot-floating-panel${isAnimatingExpand ? " chatbot-panel-animating" : ""}${exiting ? " chatbot-panel-exiting" : ""}${resumed ? " chatbot-panel-resumed" : ""}`}
        onAnimationEnd={handlePanelAnimationEnd}
        style={{
          position: "fixed",
          left: position.x,
          top: position.y,
          width: effectiveWidth,
          height: panelHeight,
          zIndex: 1000,
          display: "flex",
          flexDirection: "column",
          backgroundColor: "var(--indigo-gray-25, #fff)",
          borderRadius: 12,
          boxShadow:
            "0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 9px 28px 8px rgba(0, 0, 0, 0.05)",
          border: "1px solid var(--indigo-border-default)",
          overflow: "hidden",
        }}
      >
        <IndyChatSurface
          layout="panel"
          active={open}
          context={context}
          chat={chat}
          availableSkills={availableSkills}
          refreshAvailableSkills={refreshAvailableSkills}
          markdownComponents={markdownComponents}
          linkContext={linkContext}
          userFirstName={userFirstName}
          sidebarOpen={sidebarOpen}
          header={titleBar}
        />

        {/* ── Resize handles (edges + corners) — hidden when expanded ── */}
        {!isExpanded && (
          <>
            <div
              onMouseDown={handleResizeStart({ right: true })}
              style={{
                position: "absolute",
                right: 0,
                top: 12,
                bottom: 12,
                width: 5,
                cursor: "ew-resize",
              }}
            />
            <div
              onMouseDown={handleResizeStart({ bottom: true })}
              style={{
                position: "absolute",
                bottom: 0,
                left: 12,
                right: 12,
                height: 5,
                cursor: "ns-resize",
              }}
            />
            <div
              onMouseDown={handleResizeStart({ left: true })}
              style={{
                position: "absolute",
                left: 0,
                top: 12,
                bottom: 12,
                width: 5,
                cursor: "ew-resize",
              }}
            />
            <div
              onMouseDown={handleResizeStart({ top: true })}
              style={{
                position: "absolute",
                top: 0,
                left: 12,
                right: 12,
                height: 5,
                cursor: "ns-resize",
              }}
            />
            <div
              onMouseDown={handleResizeStart({ bottom: true, right: true })}
              style={{
                position: "absolute",
                bottom: 0,
                right: 0,
                width: 12,
                height: 12,
                cursor: "se-resize",
              }}
            />
            <div
              onMouseDown={handleResizeStart({ bottom: true, left: true })}
              style={{
                position: "absolute",
                bottom: 0,
                left: 0,
                width: 12,
                height: 12,
                cursor: "sw-resize",
              }}
            />
            <div
              onMouseDown={handleResizeStart({ top: true, right: true })}
              style={{
                position: "absolute",
                top: 0,
                right: 0,
                width: 12,
                height: 12,
                cursor: "ne-resize",
              }}
            />
            <div
              onMouseDown={handleResizeStart({ top: true, left: true })}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: 12,
                height: 12,
                cursor: "nw-resize",
              }}
            />
          </>
        )}
      </div>
    </>
  );
};

export default ChatbotModal;

export const IndyLabel: FC = () => (
  <>
    Ask <IndyWordmark />
  </>
);
